














































import { Component, Vue, Prop, Watch, Ref } from "vue-property-decorator";
import { Getter } from "vuex-class";
import {
  round,
  formatLocaleNumber,
  parseLocaleNumber,
  getSeparators,
} from "@/components/numbers/numberFormatUtils";

@Component
export default class NumericInputImproved extends Vue {
  @Prop() value?: number | null;

  // behavior properties
  // empty field return type
  @Prop({ default: "null" }) emptyReturn?: "null" | "undefined" | "";
  // if set to true then the value will be set to 0 instead of emptyReturn on blur if the field is empty
  @Prop({ default: false }) defaultToZeroWhenEmpty?: boolean;
  // number of allowed decimal places, if set to 0 the value is treated as an integer
  @Prop({ default: 2 }) decimalPlaces?: number;
  // number of displayed decimal places, display is rounded to this number of decimal places, does not affect the actual value
  @Prop({ default: null }) displayDecimalPlaces?: number | null;
  // controls if the value should be transformed to positive or negative
  @Prop({ default: "none" }) transform?: "none" | "toPositive" | "toNegative";
  // controls if the value is rounded on blur
  @Prop({ default: false }) roundOnBlur?: boolean;
  // forces input validation, preventing input of letters, multiple decimal separators, etc.
  @Prop({ default: false }) validateInput?: boolean;
  // format the field's string value to show the full value instead of the rounded value when the field gains focus
  @Prop({ default: true }) formatOnFocus?: boolean;

  // min/max value (for validation only)
  @Prop({
    default: () => {
      return { value: null, isExclusive: false };
    },
  })
  maxValue?: {
    value: number | null;
    isExclusive: boolean;
  };
  @Prop({
    default: () => {
      return { value: null, isExclusive: false };
    },
  })
  minValue?: {
    value: number | null;
    isExclusive: boolean;
  };

  // display/design properties
  @Prop() label?: string;
  @Prop({ default: false }) outlined?: boolean;
  @Prop({ default: false }) dense?: boolean;
  @Prop({ default: false }) disabled?: boolean;
  @Prop({ default: false }) readonly?: boolean;
  @Prop({ default: false }) hideDetails?: boolean;
  @Prop({ default: null }) suffix?: string;
  @Prop({ default: null }) prefix?: string;
  @Prop({ default: false }) reverse?: boolean;
  @Prop({ default: null }) placeholder?: string;

  // external validation
  @Prop({ default: () => [] }) rules?: any[];

  @Getter("language/language") language?: string;

  get internalLanguage() {
    return this.language ?? "hr-HR";
  }

  get valueAsInteger() {
    return this.decimalPlaces === 0;
  }

  get internalDisplayDecimalPlaces() {
    return this.displayDecimalPlaces ?? this.decimalPlaces;
  }

  get showTooltip() {
    return this.decimalPlaces != this.internalDisplayDecimalPlaces;
  }

  get fullValue() {
    const decimals = this.countDecimals(this.value ?? 0);
    const fullValue = this.$n(this.value ?? 0, {
      style: "decimal",
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).toString();
    const parts = [this.prefix ?? "", fullValue, this.suffix ?? ""];
    if (this.reverse) parts.reverse();
    return parts.filter((e) => !!e).join(" ");
  }

  internalModel = "";

  @Ref("fieldRef") inputField?: {
    hasError: boolean;
  };

  get hasErrors() {
    return this.inputField?.hasError ?? false;
  }

  hasFocus = false;

  inputCheck(event: KeyboardEvent) {
    if (!this.validateInput) return;

    const currentFirstChar = this.internalModel[0];
    const separators = getSeparators(this.internalLanguage);
    const actionKey = [
      "ArrowUp",
      "ArrowDown",
      "ArrowLeft",
      "ArrowRight",
      "Home",
      "End",
      "Delete",
      "Backspace",
      "Tab",
    ];

    const isActionKey = actionKey.includes(event.key);
    const plusOrMinus =
      event.key === "+" || event.key === "-" || event.key === "−";
    const isSelectCopyPaste = /[AaCcVvXxZz]/.test(event.key) && event.ctrlKey;
    const isNumeric = /[0-9]/.test(event.key);

    // just go through, nothing special
    if (
      isActionKey ||
      isSelectCopyPaste ||
      isNumeric ||
      event.key === separators.thousands
    ) {
      return;
    }

    // only 1 decimal separator allowed
    if (this.decimalPlaces !== 0) {
      if (
        event.key === separators.decimals &&
        !this.internalModel.includes(event.key)
      ) {
        return;
      }
    }

    // plus and minus prevent the actual input and flip the value to positive or negative
    if (plusOrMinus) {
      event.preventDefault();
      event.stopPropagation();

      const target = event.target as HTMLInputElement;

      if (
        event.key === "+" &&
        (currentFirstChar === "-" || currentFirstChar === "−")
      ) {
        if (target.selectionStart != target.selectionEnd && target.selectionStart !== null && target.selectionEnd !== null){
          this.clearSelection(target.selectionStart, target.selectionEnd);
        }
        if (this.internalModel.length > 0) this.internalModel = this.internalModel.substring(1);
        const n = parseLocaleNumber(this.internalModel, this.internalLanguage);
        this.$emit("input", n);
      } else if (
        (event.key === "-" || event.key === "−") &&
        currentFirstChar !== "-" &&
        currentFirstChar !== "−"
      ) {
        if (target.selectionStart != target.selectionEnd && target.selectionStart !== null && target.selectionEnd !== null){
          this.clearSelection(target.selectionStart, target.selectionEnd);
        }
        this.internalModel = event.key + this.internalModel;
        const n = parseLocaleNumber(this.internalModel, this.internalLanguage);
        this.$emit("input", n);
      }

      return;
    }

    // in other cases prevent the input
    event.preventDefault();
    event.stopPropagation();
  }

  clearSelection(selectionStart: number, selectionEnd: number){
    let remaining = "";
    if (selectionStart > 0) remaining += this.internalModel.substring(0, selectionStart);
    if (selectionEnd < this.internalModel.length - 1) remaining += this.internalModel.substring(selectionEnd);
    this.internalModel = remaining;
  }

  @Watch("value", { immediate: true })
  inputValueChanged(fresh: number | null | undefined) {
    // 1 tick delay for validation to run
    this.$nextTick(() => {
      const decimals =
        this.countDecimals(fresh ?? 0) > (this.decimalPlaces ?? 2)
          ? this.countDecimals(fresh ?? 0)
          : this.decimalPlaces ?? 2;

      if (this.hasErrors) {
        if (fresh !== null && fresh !== undefined && !Number.isNaN(fresh)) {
          this.formatDisplay(decimals);
          return;
        }
      }

      if (Number.isNaN(fresh) || fresh == null || fresh == undefined) {
        this.internalModel = "";
      } else {
        const forComparison = formatLocaleNumber(
          parseLocaleNumber(this.internalModel, this.internalLanguage),
          decimals,
          this.internalLanguage
        );

        const negativeZeroCheck = formatLocaleNumber(
          -0,
          decimals,
          this.internalLanguage
        );
        const freshFormated = formatLocaleNumber(
          fresh,
          decimals,
          this.internalLanguage
        );

        if (
          (freshFormated === forComparison && fresh !== 0) ||
          freshFormated === negativeZeroCheck
        ) {
          this.formatDisplay(this.internalDisplayDecimalPlaces);
          return;
        }
      }

      this.formatDisplay(this.internalDisplayDecimalPlaces);
    });
  }

  valueChanged() {
    if (this.internalModel === "-" || this.internalModel === "+") {
      this.$emit("input", -0);
    } else if (this.internalModel != "") {
      const n = parseLocaleNumber(this.internalModel, this.internalLanguage);

      // round to nearest integer if needed
      if (this.valueAsInteger) {
        this.$emit("input", round(n, 0));
      } else {
        this.$emit("input", n);
      }
    } else {
      this.$emit(
        "input",
        this.emptyReturn === "null"
          ? null
          : this.emptyReturn === "undefined"
          ? undefined
          : ""
      );
    }
  }

  focusHandler() {
    if (!this.hasErrors && this.formatOnFocus)
      this.formatDisplay(this.decimalPlaces);
    this.hasFocus = true;
  }

  blurHandler() {
    this.hasFocus = false;
    this.format();
    this.$emit("blur");
  }

  format() {
    if (this.internalModel !== "" && (!this.hasErrors || this.roundOnBlur)) {
      // valid number check
      let n = parseLocaleNumber(this.internalModel, this.internalLanguage);

      // transform value as needed
      if (
        (n > 0 && this.transform === "toNegative") ||
        (n < 0 && this.transform === "toPositive")
      ) {
        n *= -1;
        this.$emit("input", round(n, this.decimalPlaces ?? 2));
      } else if (this.roundOnBlur) {
        this.$emit("input", round(n, this.decimalPlaces ?? 2));
      }

      this.internalModel = formatLocaleNumber(
        n,
        this.decimalPlaces,
        this.internalLanguage
      );

      this.formatDisplay(this.internalDisplayDecimalPlaces);
    }

    if (this.internalModel === "" && this.defaultToZeroWhenEmpty) {
      this.internalModel = "0";
      this.$emit("input", 0);
    }
  }

  formatDisplay(decimals: number | undefined) {
    // I don't want my input changing as I'm typing so only do it if the element isn't focused
    if (!this.hasFocus) {
      this.internalModel = formatLocaleNumber(
        this.value,
        decimals,
        this.internalLanguage
      );
    }
  }

  // internal validation
  maxDecimals(value: number | null | undefined) {
    if (value === null || value === undefined) return true;

    if (value !== undefined) {
      return !(this.countDecimals(value) > (this.decimalPlaces ?? 2))
        ? true
        : this.$t("Validation.MaxDecimals{max}", {
            max: this.decimalPlaces ?? 2,
          });
    } else {
      return true;
    }
  }

  countDecimals(num: number) {
    return num.toString().split(".")[1]?.length || 0;
  }

  max(num: number | null | undefined) {
    if (
      num === null ||
      num === undefined ||
      this.maxValue?.value === null ||
      this.maxValue?.value === undefined
    )
      return true;

    const maxValue = this.maxValue.isExclusive
      ? this.maxValue.value - 1 / 10 ** (this.decimalPlaces ?? 0)
      : this.maxValue.value;

    if (maxValue < num)
      return this.$t("Validation.NumericInput.MaxValue", {
        max: formatLocaleNumber(
          maxValue,
          this.decimalPlaces,
          this.internalLanguage
        ),
      });
    return true;
  }

  min(num: number | null | undefined) {
    if (
      num === null ||
      num === undefined ||
      this.minValue?.value === null ||
      this.minValue?.value === undefined
    )
      return true;

    const minValue = this.minValue.isExclusive
      ? this.minValue.value + 1 / 10 ** (this.decimalPlaces ?? 0)
      : this.minValue.value;

    if (minValue > num)
      return this.$t("Validation.NumericInput.MinValue", {
        min: formatLocaleNumber(
          minValue,
          this.decimalPlaces,
          this.internalLanguage
        ),
      });
    return true;
  }
}
